import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X, CreditCard, ChevronUp } from 'lucide-react'
import { useContext } from 'react'
import { FamilyContext } from '../Context/FamilyHeadContext'
import { API_URL } from "../services/apiService"
import bitLogo from '../Image/bit.png'
import payboxLogo from '../Image/paybox.png'
import paypalLogo from '../Image/paypal.png'
import bitQR from '../Image/bitQr.jpg'
import payboxQR from '../Image/payboxQr.png'

const DonationPopup = () => {
    const [isOpen, setIsOpen] = useState(false)
    const [isMinimized, setIsMinimized] = useState(true)
    const [isExpanded, setIsExpanded] = useState(false)
    const { t } = useTranslation()
    const { currentFamilyHead } = useContext(FamilyContext)
    const [showQR, setShowQR] = useState('')
    const [showBankDetails, setShowBankDetails] = useState(false)
    const [hasShownFirstPopup, setHasShownFirstPopup] = useState(false)

    useEffect(() => {
        const resetTimer = () => {
            localStorage.setItem('firstVisitTime', Date.now().toString())
            localStorage.removeItem('popupLastClosed')
            setHasShownFirstPopup(false)
        }

        resetTimer()

        const checkAndShowPopup = () => {
            const lastClosedTime = localStorage.getItem('popupLastClosed')
            const firstVisitTime = localStorage.getItem('firstVisitTime')
            const currentTime = Date.now()

            if (!hasShownFirstPopup && firstVisitTime && (currentTime - parseInt(firstVisitTime)) >= 900000) {
                setIsOpen(true)
                setHasShownFirstPopup(true)
            } else if (lastClosedTime && (currentTime - parseInt(lastClosedTime)) >= 1800000) {
                setIsOpen(true)
            }
        }

        const intervalId = setInterval(checkAndShowPopup, 300000)
        return () => clearInterval(intervalId)
    }, [hasShownFirstPopup])

    const handleClose = () => {
        localStorage.setItem('popupLastClosed', Date.now().toString())
        setIsOpen(false)
    }

    const handleBitPayment = () => {
        if (window.innerWidth < 640) { // sm breakpoint
            window.open('https://www.bitpay.co.il/app/me/76B01423-1E90-0D85-6FDC-7BDBCA5939E3898B', '_blank')
        } else {
            setShowQR(bitQR)
        }
    }

    const handlePayboxPayment = () => {
        if (window.innerWidth < 640) {
            window.open('https://payboxapp.page.link/NR4HRCP5WX4YKUTF9', '_blank')
        } else {
            setShowQR(payboxQR)
        }
    }

    const handlePayPalPayment = () => {
        window.open('https://www.paypal.me/HarelKohen', '_blank')
    }

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded)
    }

    const paymentOptions = [
        {
            name: t('payPopup.paymentMethods.bit'),
            handler: handleBitPayment,
            bgColor: 'bg-[#00353b]',
            logo: bitLogo,
            hoverColor: 'hover:bg-[#0c4c54]'
        },
        {
            name: t('payPopup.paymentMethods.paybox'),
            handler: handlePayboxPayment,
            bgColor: 'bg-[#4ca4dc]',
            logo: payboxLogo,
            hoverColor: 'hover:bg-[#69b2e3]'
        },
        {
            name: t('payPopup.paymentMethods.paypal'),
            handler: handlePayPalPayment,
            bgColor: 'bg-[#001f5c]',
            logo: paypalLogo,
            hoverColor: 'hover:bg-[#0e3175]'
        },
        {
            name: t('payPopup.paymentMethods.bankTransfer'),
            handler: () => setShowBankDetails(true),
            bgColor: 'bg-[#937c5d]',
            hoverColor: 'hover:bg-[#826e52]',
            icon: <CreditCard className="w-6 h-6 text-white"/>
        }
    ]

    return (
        <>
            {isOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl w-full max-w-md mx-4 relative">
                        {/* Header Section - Always Visible */}
                        <div className="p-4 border-b">
                            <button
                                onClick={handleClose}
                                className="absolute top-2 right-2 p-2 hover:bg-gray-100 rounded-full"
                            >
                                <X className="w-5 h-5 text-gray-500"/>
                            </button>
                            <div className="flex items-center justify-center gap-4">
                                {currentFamilyHead?.images?.[0]?.key ? (
                                    <img
                                        crossOrigin="use-credentials"
                                        src={`${API_URL}/image/download/${encodeURIComponent(currentFamilyHead.images[0].key)}`}
                                        alt={currentFamilyHead.name}
                                        className="w-16 h-18 rounded-lg object-cover"
                                    />
                                ) : (
                                    <CreditCard className="w-12 h-12 text-[#937c5d]"/>
                                )}
                                <h2 className="text-xl font-bold text-[#937c5d]">
                                    {t('payPopup.title')}
                                </h2>
                            </div>
                        </div>

                        {/* Content Section - Scrollable if needed */}
                        <div className="p-4 max-h-104 overflow-y-auto">
                            <p className="text-[#4a3f30] text-sm leading-relaxed mb-6">
                                {t('payPopup.textUp')}
                                {t('payPopup.textDown')}
                            </p>

                            <div className="grid grid-cols-1 gap-3">
                                {paymentOptions.map((option, index) => (
                                    <button
                                        key={index}
                                        onClick={option.handler}
                                        className={`w-full px-4 py-3 text-white rounded-xl
                                            flex items-center justify-between
                                            ${option.bgColor} ${option.hoverColor}`}
                                    >
                                        <span className="font-medium">{option.name}</span>
                                        {option.logo ? (
                                            <img
                                                crossOrigin="use-credentials"
                                                src={option.logo}
                                                alt={option.name}
                                                className="w-8 h-8 object-contain"
                                            />
                                        ) : option.icon}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showBankDetails && (
                <div
                    className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] overflow-y-auto"
                    onClick={() => setShowBankDetails(false)}
                >
                    <div
                        className="relative bg-white rounded-xl p-8 max-w-md w-full mx-4 my-4"
                        onClick={e => e.stopPropagation()}
                    >
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowBankDetails(false);
                            }}
                            className="absolute -top-2 -right-2 p-2 bg-teal-800 rounded-full hover:bg-teal-600"
                        >
                            <X className="w-5 h-5 text-white" />
                        </button>

                        <h2 className="text-2xl font-heebo font-bold text-[#937c5d] mb-6 text-center">
                            {t('payPopup.bankTransferDetails')}
                        </h2>

                        <div className="space-y-4">
                            <div className="bg-[#937c5d]/5 p-6 rounded-xl mx-auto max-w-sm">
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-[#4a3f30]">{t('payPopup.bankDetails.bank')}</span>
                                        <span className="font-medium text-[#937c5d]">{t('payPopup.bankDetails.bankName')}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-[#4a3f30]">{t('payPopup.bankDetails.branch')}</span>
                                        <span className="font-medium text-[#937c5d]">76</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-[#4a3f30]">{t('payPopup.bankDetails.account')}</span>
                                        <span className="font-medium text-[#937c5d]">93231</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-[#4a3f30]">{t('payPopup.bankDetails.name')}</span>
                                        <span className="font-medium text-[#937c5d]">{t('payPopup.bankDetails.accountName')}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showQR && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] overflow-y-auto"
                     onClick={() => setShowQR('')}>
                    <div className="relative bg-white rounded-xl p-4 max-w-md mx-4 my-4">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowQR('');
                            }}
                            className="absolute -top-2 -right-2 p-2 bg-teal-800 rounded-full hover:bg-teal-600"
                        >
                            <X className="w-5 h-5 text-white" />
                        </button>
                        <h2 className="text-2xl font-heebo font-bold text-[#937c5d] mb-6">
                            {t('payPopup.scanQRCode')}
                        </h2>
                        <img
                            crossOrigin="use-credentials"
                            src={showQR}
                            alt="QR Code"
                            className="w-96 h-96 object-contain"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </div>
            )}

            {isMinimized && !isOpen && (
                <>
                    <div className="sm:hidden fixed bottom-0 right-0 left-0 z-20 animate-slide-in-bottom">
                        <div className="bg-white p-3 shadow-lg w-full border-t">
                            <div onClick={toggleExpanded} className="cursor-pointer">
                                <div
                                    onClick={toggleExpanded}
                                    className="flex items-center justify-between cursor-pointer"
                                >
                                    <h3 className="font-bold text-[#937c5d]">
                                        {t('payPopup.mobileView')}
                                    </h3>
                                    <ChevronUp
                                        className={`w-4 h-4 text-[#937c5d] transition-transform 
                                ${isExpanded ? 'rotate-180' : ''}`}
                                    />
                                </div>
                                {isExpanded && (
                                    <div className="mt-3 grid grid-cols-2 gap-2">
                                        {paymentOptions.map((option, index) => (
                                            <button
                                                key={index}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    option.handler()
                                                }}
                                                className={`px-3 py-2 text-white rounded-lg
                                            flex items-center justify-center gap-2
                                            ${option.bgColor} ${option.hoverColor}`}
                                            >
                                                <span className="text-sm">{option.name}</span>
                                                {option.logo ? (
                                                    <img
                                                        crossOrigin="use-credentials"
                                                        src={option.logo}
                                                        alt={option.name}
                                                        className="w-6 h-6 object-contain"
                                                    />
                                                ) : option.icon}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="hidden sm:flex fixed bottom-6 right-6 z-20 animate-slide-in-bottom gap-1">
                        {paymentOptions.map((option, index) => (
                            <button
                                key={index}
                                onClick={option.handler}
                                className={`px-2 py-2 text-white rounded-lg font-heebo text-sm
                           flex items-center justify-center gap-2 transition-colors
                           ${option.bgColor} ${option.hoverColor}`}
                            >
                                <span>{option.name}</span>
                                {option.logo ? (
                                    <img
                                        crossOrigin="use-credentials"
                                        src={option.logo}
                                        alt={option.name}
                                        className="w-4 h-4 object-contain"
                                    />
                                ) : (
                                    <CreditCard className="w-4 h-4"/>
                                )}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </>
    );
}
export default DonationPopup